import { useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Textarea } from "@/web/components/primitives/textarea";
import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";
import { cn } from "@/web/lib/styling";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import type { QuestionNumberUnit } from "@tycho/web-bindings/types";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { BudgetSlider } from "@/web/routes/_protected/smb/-dashboard/-BudgetSlider";
import { CATEGORY_PALETTES } from "@/web/routes/_protected/smb/-dashboard/-categoryPalettes";
import { QuestionDetailsContent } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionDetailsContent";
import { QuestionSubtitleMarkdown } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionSubtitleMarkdown";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
  SmbQuestionDetails,
} from "@/web/routes/_protected/smb/-dashboard/-types";

const BUDGET_CARD_PALETTE = CATEGORY_PALETTES.budget;

export type NumberQuestionProps = {
  questionId: string;
  badge: string;
  question: string;
  subtitle?: string;
  min: number;
  max: number;
  step?: number;
  defaultValue?: number;
  units: QuestionNumberUnit["tag"];
  details?: SmbQuestionDetails;
};

function NumberQuestion({
  questionId,
  badge,
  question,
  subtitle,
  min,
  max,
  step = 1000,
  onSkip,
  defaultValue,
  units,
  details,
  onComplete,
}: NumberQuestionProps & SmbActionCoreProps) {
  // Round the default value to the nearest step
  const roundedDefaultValue = Math.round((defaultValue ?? min) / step) * step;
  const [value, setValue] = useState(roundedDefaultValue);
  const [note, setNote] = useState("");
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);

  const { mutate: submitAnswer } = useProcedureMutation(
    procedures.submitUnderwritingQuestionAnswer,
  );

  const handleContinue = () => {
    // TODO(fas): error handling?
    submitAnswer({
      answer: { tag: "Number", value: { value: value, note: note.trim() || undefined } },
      questionId: questionId,
    });
    onComplete({ value, note: note.trim() || undefined });
  };

  return (
    <>
      <ActionCard category="budget">
        <ActionCard.Header>
          <ActionCard.Badge>{badge}</ActionCard.Badge>
          {details && <ActionCard.DetailsTrigger />}
        </ActionCard.Header>

        <ActionCard.Main>
          <ActionCard.Title>{question}</ActionCard.Title>
          {subtitle && (
            <ActionCard.Description>
              <QuestionSubtitleMarkdown markdown={subtitle} />
            </ActionCard.Description>
          )}
        </ActionCard.Main>

        <ActionCard.Body className="flex flex-col justify-center">
          <BudgetSlider
            value={value}
            onChange={setValue}
            min={min}
            max={max}
            step={step}
            format={(v) => formatCompactWholeNumberWithUnit(formatNumberToShort(v), units)}
          />
        </ActionCard.Body>

        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <Button
            variant="outline"
            color={BUDGET_CARD_PALETTE.skipButtonColor}
            size="md"
            data-slot="smb-action-card-drawer-trigger"
            className={BUDGET_CARD_PALETTE.skipButtonClass}
            aria-expanded={noteDrawerOpen}
            aria-haspopup="dialog"
            onClick={() => setNoteDrawerOpen(true)}
          >
            Add note
          </Button>
          <ActionCard.PrimaryButton onClick={handleContinue}>Continue</ActionCard.PrimaryButton>
        </ActionCard.Footer>

        {details && (
          <ActionCard.Back
            title={details.title}
            description={
              details.subtitle ? (
                <QuestionSubtitleMarkdown markdown={details.subtitle} />
              ) : undefined
            }
          >
            <QuestionDetailsContent details={details} />
          </ActionCard.Back>
        )}
      </ActionCard>

      <Drawer open={noteDrawerOpen} onOpenChange={setNoteDrawerOpen}>
        <DrawerContent className={cn(BUDGET_CARD_PALETTE.cardBg, BUDGET_CARD_PALETTE.onCardFg)}>
          <DrawerHeader>
            <DrawerTitle>Add a note</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody className="flex min-h-0 flex-col gap-3">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note to include with your response"
              aria-label="Add a note to include with your response"
              className="min-h-31 resize-none border-withus-white/30 bg-white/5 p-4 text-withus-white placeholder:text-withus-white/40 focus-visible:border-withus-white focus-visible:ring-0"
            />
          </DrawerBody>
          <DrawerFooter>
            <Button
              variant="solid"
              color={BUDGET_CARD_PALETTE.primaryButtonColor}
              size="md"
              className={cn("w-full", BUDGET_CARD_PALETTE.primaryButtonClass)}
              onClick={() => {
                setNoteDrawerOpen(false);
                handleContinue();
              }}
            >
              Continue
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export function createNumberQuestion(
  props: NumberQuestionProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = props.questionId, canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <NumberQuestion {...rest} {...core} />,
  };
}
